import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { environment } from '@environment/environment';
import { LoadingService } from '@services/shared/loading.service';
import { MessageAlertService } from '@services/shared/message-alert.service';
import { Observable, of, throwError } from 'rxjs';
import { catchError, finalize, tap } from 'rxjs/operators';

export interface DevLoginAccount {
	id: string;
	name: string;
	email: string;
	role: string;
	type: 'user' | 'client';
}

@Injectable({
	providedIn: 'root',
})
export class AuthService {
	private readonly TOKEN_KEY = 'JWT_TOKEN';
	private readonly REFRESH_TOKEN_KEY = 'refresh_token';
	private readonly loginUrl = `${environment.pathBackend}/auth/login`;
	private readonly refreshUrl = `${environment.pathBackend}/auth/refresh`;
	private readonly devLoginUrl = `${environment.pathBackend}/auth/dev-login`;
	private refreshTokenTimeout: any;
	private retryCount = 0;
	private readonly maxRetries = 3;

	constructor(
		private http: HttpClient,
		private router: Router,
		private messageAlertService: MessageAlertService,
	) {}

	/**
	 * Inicia sesión enviando las credenciales al backend.
	 * @param email Correo del usuario
	 * @param password Contraseña del usuario
	 * @returns Observable con la respuesta del backend
	 */
	login(email: string, password: string): Observable<any> {
		const loginData = { email, password };

		return this.http.post<any>(this.loginUrl, loginData).pipe(
			tap((response) => {
				this.storeLoginResponse(response);
			}),
			catchError((error: HttpErrorResponse) => {
				console.error('❌ Error en el servicio de login:', error);
				const backendMessage = error.error?.message;

				// Diccionario de traducción
				const translations: Record<string, string> = {
					'Incorrect password': 'Contraseña incorrecta',
					'User not found': 'Usuario no encontrado',
				};

				const translatedMessage = (backendMessage && translations[backendMessage]) || backendMessage;

				const errorMessage = translatedMessage || 'Error interno del servidor';
				this.messageAlertService.showMessage('error', 'Error', errorMessage);
				return throwError(() => new Error(errorMessage));
			}),
		);
	}

	getDevLoginAccounts(): Observable<DevLoginAccount[]> {
		return this.http.get<DevLoginAccount[]>(`${this.devLoginUrl}/accounts`).pipe(
			catchError((error: HttpErrorResponse) => {
				const errorMessage = error.error?.message || 'No se pudieron cargar los usuarios de desarrollo';
				this.messageAlertService.showMessage('error', 'Dev login', errorMessage);
				return throwError(() => new Error(errorMessage));
			}),
		);
	}

	devLogin(account: DevLoginAccount): Observable<any> {
		return this.http
			.post<any>(this.devLoginUrl, {
				id: account.id,
				type: account.type,
			})
			.pipe(
				tap((response) => this.storeLoginResponse(response)),
				catchError((error: HttpErrorResponse) => {
					const errorMessage = error.error?.message || 'No se pudo iniciar la sesión de desarrollo';
					this.messageAlertService.showMessage('error', 'Dev login', errorMessage);
					return throwError(() => new Error(errorMessage));
				}),
			);
	}

	/**
	 * Solicita un enlace de restablecimiento de contraseña.
	 * @param email Correo del usuario
	 * @returns Observable con la respuesta del backend
	 */
	resetPassword(email: string): Observable<any> {
		return this.http.post<any>(`${environment.pathBackend}/auth/reset-password`, { email }).pipe(
			tap(() => {
				this.messageAlertService.showMessage(
					'success',
					'Enlace enviado',
					'Si el correo existe, recibirás un enlace para restablecer tu contraseña.',
				);
			}),
			catchError((error: HttpErrorResponse) => {
				const errorMessage = error.error?.message || 'Error al enviar el enlace de restablecimiento';
				this.messageAlertService.showMessage('error', 'Error', errorMessage);
				return throwError(() => new Error(errorMessage));
			}),
		);
	}

	/**
	 * Actualiza la contraseña usando el token del enlace de restablecimiento.
	 * @param accessToken Token de acceso de Supabase
	 * @param newPassword Nueva contraseña
	 * @returns Observable con la respuesta del backend
	 */
	updatePasswordWithToken(accessToken: string, newPassword: string): Observable<any> {
		return this.http
			.post<any>(`${environment.pathBackend}/auth/update-password`, {
				accessToken,
				newPassword,
			})
			.pipe(
				tap(() => {
					this.messageAlertService.showMessage(
						'success',
						'Contraseña actualizada',
						'Tu contraseña ha sido actualizada correctamente.',
					);
				}),
				catchError((error: HttpErrorResponse) => {
					const backendMessage = error.error?.message;
					const translations: Record<string, string> = {
						'Token de restablecimiento inválido o expirado':
							'El enlace de restablecimiento ha expirado o es inválido',
					};
					const translatedMessage =
						(backendMessage && translations[backendMessage]) ||
						backendMessage ||
						'Error al actualizar la contraseña';
					this.messageAlertService.showMessage('error', 'Error', translatedMessage);
					return throwError(() => new Error(translatedMessage));
				}),
			);
	}

	/**
	 * Cierra la sesión enviando el refresh token al backend y eliminando los tokens del almacenamiento local.
	 * @returns Observable con la respuesta del backend
	 */
	logout(): Observable<any> {
		this.stopRefreshTokenTimer();
		localStorage.removeItem(this.TOKEN_KEY);
		localStorage.removeItem(this.REFRESH_TOKEN_KEY);

		// 🧹 Limpiar datos temporales de consulta y plan
		localStorage.removeItem('pendingConsultationData');
		localStorage.removeItem('selectedPlan');
		localStorage.removeItem('consultationDetails');

		// 🧹 Limpiar info del usuario
		localStorage.removeItem('USER_ID');
		localStorage.removeItem('USER_NAME');
		localStorage.removeItem('USER_ROLE');
		localStorage.removeItem('USER_CATEGORIES');

		this.router.navigate(['/login']);
		return of(null);
	}

	/**
	 * Verifica si el usuario está autenticado.
	 * @returns `true` si el token existe y no ha expirado.
	 */
	isAuthenticated(): boolean {
		const token = localStorage.getItem(this.TOKEN_KEY);
		if (!token) {
			return false;
		}

		try {
			const payload = JSON.parse(atob(token.split('.')[1]));
			const isExpired = payload.exp * 1000 < Date.now();

			if (isExpired) {
				return false;
			}

			return true;
		} catch (error) {
			console.error('❌ Error decodificando el token:', error);
			return false;
		}
	}

	/**
	 * Refresca el access token usando el refresh token almacenado.
	 * Reintenta con backoff exponencial (2s, 4s, 8s) hasta 3 veces.
	 * Si todos los intentos fallan, hace logout.
	 */
	refreshToken(): Observable<any> {
		const refreshToken = localStorage.getItem(this.REFRESH_TOKEN_KEY);

		if (!refreshToken) {
			return throwError(() => new Error('No refresh token available'));
		}

		return this.http.post<any>(this.refreshUrl, { refresh_token: refreshToken }).pipe(
			tap((response) => {
				if (!response?.access_token || !response?.refresh_token) {
					throw new Error('Invalid refresh response');
				}
				this.retryCount = 0;
				localStorage.setItem(this.TOKEN_KEY, response.access_token);
				localStorage.setItem(this.REFRESH_TOKEN_KEY, response.refresh_token);
				this.startRefreshTokenTimer(response);
			}),
			catchError((error) => {
				console.error('❌ Token refresh failed:', error);

				if (this.retryCount < this.maxRetries) {
					this.retryCount++;
					const backoffTime = 2 ** this.retryCount * 1000;
					return new Observable((subscriber) => {
						setTimeout(() => {
							this.refreshToken().subscribe(subscriber);
						}, backoffTime);
					});
				}

				this.retryCount = 0;
				this.logout();
				return throwError(() => error);
			}),
		);
	}

	private storeLoginResponse(response: any): void {
		if (!response?.access_token) {
			console.error('❌ Error: No se recibió access_token en la respuesta');
			return;
		}

		const token = response.access_token;
		localStorage.setItem(this.TOKEN_KEY, token);

		if (response.refresh_token) {
			localStorage.setItem(this.REFRESH_TOKEN_KEY, response.refresh_token);
			this.startRefreshTokenTimer(response);
		}

		const userOrClient = response.user || response.client;
		if (userOrClient) {
			const id = userOrClient.id?.toString();
			const name = userOrClient.name || userOrClient.email;
			const role = userOrClient.role || 'client';

			if (id) localStorage.setItem('USER_ID', id);
			if (name) localStorage.setItem('USER_NAME', name);
			if (role) localStorage.setItem('USER_ROLE', role);
		} else {
			console.error('❌ No se recibió información de usuario o cliente');
		}

		if (userOrClient?.categories) {
			const categoryIds = userOrClient.categories.map((category: any) => category.id);
			localStorage.setItem('USER_CATEGORIES', JSON.stringify(categoryIds));
		} else {
			localStorage.removeItem('USER_CATEGORIES');
		}

		if (localStorage.getItem(this.TOKEN_KEY) !== token) {
			console.error('❌ Error: El token no se guardó correctamente');
		}
	}

	/**
	 * Programa el refresco proactivo del token al 66% de su tiempo de vida.
	 * Si el token expira en <2min, refresca inmediatamente.
	 */
	private startRefreshTokenTimer(tokens: any) {
		const jwtToken = JSON.parse(atob(tokens.access_token.split('.')[1]));
		const expiresAt = new Date(jwtToken.exp * 1000);
		const timeUntilExpiry = expiresAt.getTime() - Date.now();

		if (timeUntilExpiry < 120000) {
			this.refreshToken().subscribe();
			return;
		}

		const refreshDelay = Math.floor(timeUntilExpiry * 0.66);

		if (this.refreshTokenTimeout) {
			clearTimeout(this.refreshTokenTimeout);
		}

		this.refreshTokenTimeout = setTimeout(() => {
			this.refreshToken().subscribe({
				next: () => {},
				error: (error) => {
					console.error('❌ Auto refresh failed:', error);
					setTimeout(() => this.refreshToken().subscribe(), 30000);
				},
			});
		}, refreshDelay);
	}

	private stopRefreshTokenTimer() {
		if (this.refreshTokenTimeout) {
			clearTimeout(this.refreshTokenTimeout);
		}
	}
}
